import React from 'react';
import { useTheme } from 'react-jss';
import { darken, lighten, getDisplayName, clsx, getContrastColor, conditionGuard, safeRest } from '@wds/utils';
import { ProgressCircle } from '@wds/progress-circle';
import { IconArrowBox } from '@wds/icons';
import { createMemoStyles, SEMANTIC_COLORS } from '@wds/styles';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __spreadArray(to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
}

var useStyles = createMemoStyles(function (theme) {
    var type = theme.type, radius = theme.radius, shadow = theme.shadow, palette = theme.palette, spacing = theme.spacing;
    var button = {
        height: {
            sm: 32,
            md: 40,
            lg: 48,
        },
        fontSize: {
            sm: type.fontSize.bodySm,
            md: type.fontSize.body,
            lg: type.fontSize.body,
        },
        padding: {
            sm: spacing[4],
            md: spacing[5],
            lg: spacing[6],
        },
        borderRadius: radius.small,
        boxShadow: shadow.none,
    };
    var hoverFocus = function () { return ({
        transition: 'none',
        textDecoration: 'none',
    }); };
    var active = function () { return ({
        transition: 'none',
        outline: 'none',
    }); };
    var variantSolid = function (textColor, color, pressedColor, loading) { return ({
        color: loading ? 'transparent' : textColor,
        borderColor: pressedColor,
        backgroundColor: pressedColor,
        '&:hover': {
            boxShadow: shadow.sky,
        },
        '&:hover, &:focus': __assign(__assign({}, hoverFocus()), { borderColor: lighten(0.03, color), backgroundColor: lighten(0.03, color) }),
        '&:active': __assign(__assign({}, active()), { borderColor: color, backgroundColor: color }),
    }); };
    var variantGhost = function (backgroundColor, pressedColor, loading) { return ({
        color: loading ? 'transparent' : pressedColor,
        borderColor: pressedColor,
        backgroundColor: 'transparent',
        '&:hover, &:focus': __assign(__assign({}, hoverFocus()), { backgroundColor: backgroundColor }),
        '&:active': __assign(__assign({}, active()), { borderColor: pressedColor }),
    }); };
    var variantText = function (backgroundColor, pressedColor, loading) { return ({
        color: loading ? 'transparent' : pressedColor,
        borderColor: 'transparent',
        backgroundColor: 'transparent',
        '&:hover, &:focus': __assign(__assign({}, hoverFocus()), { borderColor: backgroundColor, backgroundColor: backgroundColor }),
    }); };
    var colorAlt = function (loading, onDark, variant) {
        var backgroundColor = palette.background.light;
        var color = palette.text.dark;
        var borderColor = palette.text.dark;
        if (variant === 'ghost') {
            borderColor = onDark ? palette.background.light : palette.background.dark;
            backgroundColor = 'transparent';
            color = onDark ? palette.text.light : palette.text.dark;
        }
        else if (variant === 'text') {
            borderColor = 'transparent';
            backgroundColor = 'transparent';
            color = onDark ? palette.text.light : palette.text.dark;
        }
        return {
            color: loading ? 'transparent' : color,
            borderColor: borderColor,
            backgroundColor: backgroundColor,
            '&:hover': {
                boxShadow: shadow.sky,
            },
            '&:hover, &:focus': __assign(__assign({}, hoverFocus()), { borderColor: borderColor, backgroundColor: onDark && variant !== 'solid'
                    ? darken(0.03, palette.background.dark)
                    : palette.background.main }),
            '&:active': __assign(__assign({}, active()), { borderColor: borderColor, backgroundColor: onDark && variant !== 'solid'
                    ? darken(0.03, palette.background.dark)
                    : palette.background.light }),
        };
    };
    var svgMargin = function (leftIcon, rightIcon, size) {
        var iconMargin = function (s) {
            return {
                sm: '8px',
                md: '16px',
                lg: '16px',
            }[s];
        };
        return {
            '& > svg:first-child': __assign({}, (leftIcon && {
                marginRight: "".concat(iconMargin(size)),
            })),
            '& > svg:last-child': __assign({}, (rightIcon && {
                marginLeft: "".concat(iconMargin(size)),
            })),
        };
    };
    var iconPadding = function (size) {
        return {
            sm: spacing[2],
            md: spacing[4],
            lg: spacing[5],
        }[size];
    };
    return {
        wrenchButton: function (_a) {
            var _b, _c, _d, _e;
            var color = _a.color, onDark = _a.onDark, variant = _a.variant, size = _a.size, ariaPressed = _a.ariaPressed, stretched = _a.stretched, iconOnly = _a.iconOnly, leftIcon = _a.leftIcon, rightIcon = _a.rightIcon, _f = _a.loading, loading = _f === void 0 ? false : _f;
            var buttonColor = (onDark && ((_b = palette[color]) === null || _b === void 0 ? void 0 : _b.onDark)) ||
                ((_c = palette[color]) === null || _c === void 0 ? void 0 : _c.main) ||
                palette.text[color] ||
                palette.text.dark;
            var pressedColor = ariaPressed
                ? darken(0.03, buttonColor)
                : buttonColor;
            var isStretched = stretched
                ? { display: 'flex', width: '100%' }
                : { display: 'inline-flex' };
            return __assign(__assign(__assign(__assign(__assign(__assign(__assign({}, isStretched), { boxSizing: 'border-box', fontWeight: type.fontWeight.medium, fontFamily: type.fontFamily.base, '-webkit-font-smoothing': 'antialiased', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', position: 'relative', borderRadius: button.borderRadius, textDecoration: 'none', border: '1px solid transparent', boxShadow: button.boxShadow, transition: 'color 0.4s, border-color 0.4s, background-color 0.4s, box-shadow 0.4s', padding: [0, iconOnly ? iconPadding(size) : button.padding[size]], fontSize: size ? button.fontSize[size] : null, minHeight: size ? button.height[size] : null, '&:hover, &:focus': hoverFocus(), '&:active': active(), '&[disabled], &[aria-disabled="true"]': {
                    pointerEvents: 'none',
                    opacity: '0.4',
                } }), (variant === 'solid' &&
                variantSolid(onDark
                    ? palette.text.dark
                    : (_e = (_d = palette[color]) === null || _d === void 0 ? void 0 : _d.contrast) !== null && _e !== void 0 ? _e : '', buttonColor, pressedColor, loading))), (variant === 'ghost' &&
                variantGhost(onDark
                    ? darken(0.03, palette.text.dark)
                    : palette.background.accent, pressedColor, loading))), (variant === 'text' &&
                variantText(onDark
                    ? darken(0.03, palette.text.dark)
                    : palette.background.accent, pressedColor, loading))), ((leftIcon || rightIcon) && svgMargin(leftIcon, rightIcon, size))), (color === 'alt' && colorAlt(loading, onDark, variant)));
        },
        wrenchButtonLoading: function () { return ({
            position: 'absolute',
            display: 'flex',
        }); },
        wrenchButtonNewTab: function (_a) {
            var size = _a.size;
            return ({
                paddingLeft: iconPadding(size) / 2,
                display: 'flex',
            });
        },
    };
});

var Button = function (_a) {
    var ariaPressed = _a.ariaPressed, _b = _a.buttonType, buttonType = _b === void 0 ? 'button' : _b, children = _a.children, _c = _a.color, color = _c === void 0 ? 'primary' : _c, _d = _a.onDark, onDark = _d === void 0 ? false : _d, _e = _a.loading, loading = _e === void 0 ? false : _e, _f = _a.disabled, disabled = _f === void 0 ? false : _f, href = _a.href, label = _a.label, _g = _a.size, size = _g === void 0 ? 'md' : _g, _h = _a.stretched, stretched = _h === void 0 ? false : _h, _j = _a.variant, variant = _j === void 0 ? 'solid' : _j, rest = __rest(_a, ["ariaPressed", "buttonType", "children", "color", "onDark", "loading", "disabled", "href", "label", "size", "stretched", "variant"]);
    var numberOfChildren = React.Children.count(children);
    var reactChildren = React.Children.toArray(children);
    var iconOnly = !!(numberOfChildren === 1 &&
        getDisplayName(reactChildren[0]).includes('WithIcon'));
    var leftIcon = !!(numberOfChildren > 1 &&
        getDisplayName(reactChildren[0]).includes('WithIcon'));
    var rightIcon = !!(numberOfChildren > 1 &&
        getDisplayName(reactChildren[reactChildren.length - 1]).includes('WithIcon'));
    if (iconOnly && !label) {
        // eslint-disable-next-line no-console
        console.warn('A label is required when the child of a Button is an icon');
    }
    var rel;
    if (href) {
        // https://mathiasbynens.github.io/rel-noopener/
        var autoAddRel = rest.target && rest.target === '_blank' && !rest.rel;
        rel = autoAddRel ? 'noopener noreferrer' : undefined;
    }
    var theme = useTheme();
    var _k = useStyles({
        color: color,
        onDark: onDark,
        variant: variant,
        size: size,
        stretched: stretched,
        ariaPressed: ariaPressed,
        theme: theme,
        iconOnly: iconOnly,
        leftIcon: leftIcon,
        rightIcon: rightIcon,
        loading: loading,
    }), wrenchButton = _k.wrenchButton, wrenchButtonLoading = _k.wrenchButtonLoading, wrenchButtonNewTab = _k.wrenchButtonNewTab;
    var classNames = clsx(wrenchButton, 
    // Static class used for internal reference
    // Ex. ButtonGroup conditional styles
    variant ? "wrenchButton--".concat(variant) : null, color ? "wrenchButton--".concat(color) : null);
    var isDisabled = loading || disabled;
    var colorValue = color === 'alt'
        ? theme.palette.background.light
        : theme.palette[color].main;
    var contrastColor = variant === 'solid' ? getContrastColor(colorValue) : 'dark';
    var loaderContent = loading ? (React.createElement("span", { className: wrenchButtonLoading },
        React.createElement(ProgressCircle, { color: contrastColor, size: "xs", continuous: true }))) : null;
    var opensInNewTab = rest.target && rest.target === '_blank';
    var newTabAriaLabel = opensInNewTab && iconOnly ? 'This link opens in a new tab' : undefined;
    var newTabIcon = (React.createElement("span", { className: wrenchButtonNewTab },
        React.createElement(IconArrowBox, { title: "This link opens in a new tab", size: "sm" })));
    if (conditionGuard(rest, !!href)) {
        return (React.createElement("a", __assign({}, safeRest(rest), { className: classNames, href: !isDisabled ? href : undefined, role: isDisabled ? 'link' : undefined, "aria-disabled": isDisabled ? 'true' : undefined, "aria-label": newTabAriaLabel, title: label, rel: rel }),
            loaderContent,
            children,
            !iconOnly && opensInNewTab && newTabIcon));
    }
    return (React.createElement("button", __assign({}, safeRest(rest), { className: classNames, 
        // eslint-disable-next-line react/button-has-type
        type: buttonType, disabled: isDisabled, "aria-pressed": ariaPressed, "aria-label": label }),
        loaderContent,
        children));
};

var BUTTON_COLORS = __spreadArray(['alt', 'link'], SEMANTIC_COLORS, true);
var VARIANTS = ['solid', 'ghost', 'text'];
var BUTTON_TYPES = ['button', 'reset', 'submit'];
var BUTTON_SIZES = ['sm', 'md', 'lg'];
var guardAnchor = function (rest, href) { return !!href; };

export { BUTTON_COLORS, BUTTON_SIZES, BUTTON_TYPES, Button, VARIANTS, guardAnchor };
